import type { useForm } from "hooks";
import type { Moment } from "moment";
import type { BooleanSwitch, BootstrapableForm } from "types";
import type { OnboardingFormProps } from "ui/forms/ECommerce/Onboarding/types";
import { ShareholderIdentity } from "./entities";

export const FormField = {
    AuthSigAddressLine1: 'authSigAddressLine1',
    AuthSigAddressLine2: 'authSigAddressLine2',
    AuthSigCity: 'authSigCity',
    AuthSigName: 'authSigName',
    AuthSigCountryOrigin: 'authSigCountryOrigin',
    AuthSigDateOfBirth: 'authSigDateOfBirth',
    AuthSigEmail: 'authSigEmail',
    AuthSigPassportExpDate: 'authSigPassportExpDate',
    AuthSigPassportNumber: 'authSigPassportNumber',
    AuthSigPhone: 'authSigPhone',
    AuthSigPostalCode: 'authSigPostalCode',
    AuthSigTitle: 'authSigTitle',
    CeoAddressLine1: 'ceoAddressLine1',
    CeoAddressLine2: 'ceoAddressLine2',
    CeoCity: 'ceoCity',
    CeoCountryOrigin: 'ceoCountryOrigin',
    CeoDateOfBirth: 'ceoDateOfBirth',
    CeoEmail: 'ceoEmail',
    CeoName: 'ceoName',
    CeoPassportExpDate: 'ceoPassportExpDate',
    CeoPassportNumber: 'ceoPassportNumber',
    CeoPhone: 'ceoPhone',
    CeoPostalCode: 'ceoPostalCode',
    CeoTitle: 'ceoTitle',
    AuthSigPassportFileKey: 'authSigPassportFileKey',
    AuthSigUtilityBillFileKey: 'authSigUtilityBillFileKey',
    CeoPassportFileKey: 'ceoPassportFileKey',
    CeoUtilityBillFileKey: 'ceoUtilityBillFileKey',
    IsPepAuthSig: 'isPepAuthSig',
    IsPepCeo: 'isPepCeo',
    OtherPeople: {
        FieldPrefix: 'otherPeople',
        Name: 'name',
        AddressLine1: 'addressLine1',
        AddressLine2: 'addressLine2',
        City: 'city',
        PostalCode: 'postalCode',
        CountryOrigin: 'countryOrigin',
        DateOfBirth: 'dateOfBirth',
        PassportNumber: 'passportNumber',
        PassportExpDate: 'passportExpDate',
        Title: 'title',
        Email: 'email',
        Phone: 'phone',
        IsPep: 'isPep',
        UtilityBillFileKey: 'utilityBillFileKey',
        PassportFileKey: 'passportFileKey',
        SharesPercentage: 'sharesPercentage'
    }
} as const;

export type Shareholder = ShareholderIdentity;

export type OnboardingDirectorsAndShareholders = {
    readonly [FormField.AuthSigAddressLine1]: string;
    readonly [FormField.AuthSigAddressLine2]: string;
    readonly [FormField.AuthSigCity]: string;
    readonly [FormField.AuthSigCountryOrigin]: string | null;
    readonly [FormField.AuthSigDateOfBirth]: Moment | null;
    readonly [FormField.AuthSigEmail]: string;
    readonly [FormField.AuthSigName]: string;
    readonly [FormField.AuthSigPassportExpDate]: Moment | null;
    readonly [FormField.AuthSigPassportNumber]: string;
    readonly [FormField.AuthSigPhone]: string;
    readonly [FormField.AuthSigPostalCode]: string;
    readonly [FormField.AuthSigTitle]: string;
    readonly [FormField.CeoAddressLine1]: string;
    readonly [FormField.CeoAddressLine2]: string;
    readonly [FormField.CeoCity]: string;
    readonly [FormField.CeoCountryOrigin]: string | null;
    readonly [FormField.CeoDateOfBirth]: Moment | null;
    readonly [FormField.CeoEmail]: string;
    readonly [FormField.CeoName]: string;
    readonly [FormField.CeoPassportExpDate]: Moment | null;
    readonly [FormField.CeoPassportNumber]: string;
    readonly [FormField.CeoPhone]: string;
    readonly [FormField.CeoPostalCode]: string;
    readonly [FormField.CeoTitle]: string;
    readonly [FormField.AuthSigPassportFileKey]: string | null;
    readonly [FormField.AuthSigUtilityBillFileKey]: string | null;
    readonly [FormField.CeoPassportFileKey]: string | null;
    readonly [FormField.CeoUtilityBillFileKey]: string | null;
    readonly [FormField.IsPepAuthSig]: BooleanSwitch;
    readonly [FormField.IsPepCeo]: BooleanSwitch;
    readonly [FormField.OtherPeople.FieldPrefix]: Array<Shareholder>;
};

export type OnboardingDirectorsAndShareholdersBootstrapData = {};

export type OnboardingDirectorsAndShareholdersForm = Omit<
    BootstrapableForm<OnboardingDirectorsAndShareholders, OnboardingDirectorsAndShareholdersBootstrapData>,
    | 'initialValues'
> & {
    readonly initialValues: OnboardingDirectorsAndShareholders;
};

export type OnboardingDirectorsAndShareholdersProps =
    & OnboardingFormProps
    & OnboardingDirectorsAndShareholdersForm
    & ReturnType<typeof useForm<OnboardingDirectorsAndShareholders>>;
